import { useState } from "react";
import { NavHeader } from "@/components/layout/nav-header";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Calendar, Clock, Trash2, Edit2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { DateBlockModal } from "@/components/admin/date-block-modal";
import { TimeBlockModal } from "@/components/admin/time-block-modal";
import { useLocation } from "wouter";

export default function ManageCalendarPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [showDateBlockModal, setShowDateBlockModal] = useState(false);
  const [showTimeBlockModal, setShowTimeBlockModal] = useState(false);
  
  // Local state for pending changes
  const [pendingBlockedDates, setPendingBlockedDates] = useState<string[]>([]);
  const [pendingBlockedTimeSlots, setPendingBlockedTimeSlots] = useState<{startTime: string, endTime: string}[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch existing blocked dates from server
  const { data: serverBlockedDates = [], isLoading: loadingDates, refetch: refetchDates } = useQuery({
    queryKey: ["blocked-dates"],
    queryFn: async () => {
      const res = await fetch("/api/admin/blocked-dates");
      if (!res.ok) throw new Error("Failed to fetch blocked dates");
      return res.json();
    },
  });

  // Fetch existing blocked time slots from server
  const { data: serverBlockedTimeSlots = [], isLoading: loadingSlots, refetch: refetchSlots } = useQuery({
    queryKey: ["blocked-time-slots"],
    queryFn: async () => {
      const res = await fetch("/api/admin/blocked-time-slots");
      if (!res.ok) throw new Error("Failed to fetch blocked time slots");
      return res.json();
    },
  });

  // Combine server data with pending changes for display
  const allBlockedDates = [
    ...serverBlockedDates,
    ...pendingBlockedDates.map((date, idx) => ({
      id: `pending-date-${idx}`,
      date: date,
      isPending: true,
    }))
  ];

  const allBlockedTimeSlots = [
    ...serverBlockedTimeSlots,
    ...pendingBlockedTimeSlots.map((slot, idx) => ({
      id: `pending-slot-${idx}`,
      startTime: slot.startTime,
      endTime: slot.endTime,
      isPending: true,
    }))
  ];

  // Handler for adding dates from modal
  const handleAddDates = (dates: string[]) => {
    setPendingBlockedDates([...pendingBlockedDates, ...dates]);
    setHasChanges(true);
  };

  // Handler for adding time slots from modal
  const handleAddTimeSlots = (slots: {startTime: string, endTime: string}[]) => {
    setPendingBlockedTimeSlots([...pendingBlockedTimeSlots, ...slots]);
    setHasChanges(true);
  };

  // Handler for removing pending items
  const handleRemovePending = (type: 'date' | 'slot', index: number) => {
    if (type === 'date') {
      setPendingBlockedDates(pendingBlockedDates.filter((_, i) => i !== index));
    } else {
      setPendingBlockedTimeSlots(pendingBlockedTimeSlots.filter((_, i) => i !== index));
    }
    setHasChanges(true);
  };

  // Save all pending changes
  const saveChangesMutation = useMutation({
    mutationFn: async () => {
      const promises = [];

      // Save pending blocked dates
      if (pendingBlockedDates.length > 0) {
        const res = await fetch("/api/admin/blocked-dates", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ dates: pendingBlockedDates }),
        });
        
        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.error || "Failed to save blocked dates");
        }
        
        const result = await res.json();
        console.log('Blocked dates result:', result);
        if (result.skipped && result.skipped.length > 0) {
          console.log(`${result.skipped.length} date(s) were already blocked`);
        }
      }

      // Save pending blocked time slots
      if (pendingBlockedTimeSlots.length > 0) {
        const res = await fetch("/api/admin/blocked-time-slots", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ slots: pendingBlockedTimeSlots }),
        });
        
        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.error || "Failed to save blocked time slots");
        }
        
        const result = await res.json();
        if (result.message && result.inserted?.length === 0) {
          console.log(result.message);
        }
      }

      return { success: true };
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Calendar settings saved successfully",
      });
      
      // Clear pending changes
      setPendingBlockedDates([]);
      setPendingBlockedTimeSlots([]);
      setHasChanges(false);
      
      // Refresh data from server
      refetchDates();
      refetchSlots();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to save some changes. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Delete existing blocked date
  const deleteBlockedDate = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/blocked-dates/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete blocked date");
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Blocked date removed successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["blocked-dates"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete existing blocked time slot
  const deleteBlockedTimeSlot = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/blocked-time-slots/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete blocked time slot");
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Blocked time slot removed successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["blocked-time-slots"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Format time for display
  const formatTimeSlot = (startTime: string, endTime: string) => {
    try {
      // The times are stored as Pacific Time wall clock times without timezone
      // We need to parse them as-is without timezone conversion
      
      // Extract just the time portion from the datetime string
      const startMatch = startTime.match(/(\d{2}):(\d{2})/);
      const endMatch = endTime.match(/(\d{2}):(\d{2})/);
      
      if (!startMatch || !endMatch) {
        return "Invalid time";
      }
      
      const startHour = parseInt(startMatch[1]);
      const startMin = parseInt(startMatch[2]);
      const endHour = parseInt(endMatch[1]);
      const endMin = parseInt(endMatch[2]);
      
      // Format times in 12-hour format
      const formatTime = (hour: number, min: number) => {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${String(min).padStart(2, '0')} ${period}`;
      };
      
      return `${formatTime(startHour, startMin)} - ${formatTime(endHour, endMin)}`;
    } catch (error) {
      console.error("Error formatting time slot:", error, { startTime, endTime });
      return "Invalid time";
    }
  };

  // Get upcoming blocked items for display
  const upcomingBlocks = [
    ...allBlockedDates.map((item: any, index: number) => {
      try {
        let dateObj: Date;
        
        if (typeof item.date === 'string' && item.date.includes('T')) {
          // If date already has time component, parse as is
          dateObj = new Date(item.date);
        } else {
          // If just a date string, parse as local date
          dateObj = new Date(item.date + 'T00:00:00');
        }
        
        if (isNaN(dateObj.getTime())) {
          return {
            id: item.isPending ? item.id : `date-${item.id}`,
            type: 'date',
            date: item.date,
            display: "Invalid date",
            rawId: item.id,
            isPending: item.isPending,
            pendingIndex: item.isPending ? parseInt(item.id.split('-')[2]) : undefined,
          };
        }
        
        return {
          id: item.isPending ? item.id : `date-${item.id}`,
          type: 'date',
          date: item.date,
          display: format(dateObj, "EEE, MMM d, yyyy"),
          rawId: item.id,
          isPending: item.isPending,
          pendingIndex: item.isPending ? parseInt(item.id.split('-')[2]) : undefined,
        };
      } catch (error) {
        return {
          id: item.isPending ? item.id : `date-${item.id}`,
          type: 'date',
          date: item.date,
          display: "Invalid date",
          rawId: item.id,
          isPending: item.isPending,
          pendingIndex: item.isPending ? parseInt(item.id.split('-')[2]) : undefined,
        };
      }
    }),
    ...allBlockedTimeSlots.map((item: any) => {
      try {
        // Parse the date portion from the datetime string
        const dateMatch = item.startTime.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!dateMatch) {
          throw new Error("Invalid date format");
        }
        
        // Create a date using the extracted date parts (treating as local date)
        const displayDate = new Date(
          parseInt(dateMatch[1]), 
          parseInt(dateMatch[2]) - 1, // Month is 0-indexed
          parseInt(dateMatch[3])
        );
          
        return {
          id: item.isPending ? item.id : `slot-${item.id}`,
          type: 'timeSlot',
          date: item.startTime,
          display: `${format(displayDate, "EEE, MMM d, yyyy")} | ${formatTimeSlot(item.startTime, item.endTime)}`,
          rawId: item.id,
          startTime: item.startTime,
          endTime: item.endTime,
          isPending: item.isPending,
          pendingIndex: item.isPending ? parseInt(item.id.split('-')[2]) : undefined,
        };
      } catch (error) {
        return {
          id: item.isPending ? item.id : `slot-${item.id}`,
          type: 'timeSlot',
          date: item.startTime,
          display: "Invalid time slot",
          rawId: item.id,
          startTime: item.startTime,
          endTime: item.endTime,
          isPending: item.isPending,
          pendingIndex: item.isPending ? parseInt(item.id.split('-')[2]) : undefined,
        };
      }
    }),
  ].sort((a, b) => {
    try {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    } catch {
      return 0;
    }
  })
  .filter(item => {
    try {
      const itemDate = new Date(item.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return itemDate >= today;
    } catch {
      return false;
    }
  });

  return (
    <div className="min-h-screen bg-clubhouse-black text-white">
      <NavHeader />
      
      <main className="container mx-auto px-4 py-8 animate-page-in">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Manage Calendar</h1>
            <p className="text-gray-400">Manage the days and times the Clubhouse is available for bookings.</p>
          </div>

          {/* Time Zone Section */}
          <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 mb-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-2">Time Zone</h2>
              <p className="text-sm text-gray-400">Select the time zone on which the Clubhouse calendar is set.</p>
            </div>
            <Select value="pacific" disabled>
              <SelectTrigger className="w-full max-w-md bg-black border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pacific">Pacific Time - US & Canada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Block Out Dates & Times Section */}
          <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Block Out Dates & Times</h2>
              <p className="text-sm text-gray-400 mb-4">Block out dates</p>
              
              <div className="flex gap-4">
                <Button
                  onClick={() => setShowDateBlockModal(true)}
                  className="bg-transparent border border-white/20 hover:bg-white/10 text-white"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add a date block
                </Button>
                <Button
                  onClick={() => setShowTimeBlockModal(true)}
                  className="bg-transparent border border-white/20 hover:bg-white/10 text-white"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add a time block
                </Button>
              </div>
            </div>

            {/* Upcoming Blockers Table */}
            {upcomingBlocks.length > 0 && (
              <div className="mt-8">
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-gray-400 font-medium">
                  <div>Upcoming blockers</div>
                  <div className="text-right">Actions</div>
                </div>
                
                <div className="space-y-2">
                  {upcomingBlocks.map((block) => (
                    <div key={block.id} className="grid grid-cols-2 gap-4 py-3 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        {block.type === 'date' ? (
                          <Calendar className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Clock className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="text-sm">
                          {block.display}
                          {block.isPending && (
                            <span className="ml-2 text-xs text-amber-400">(pending)</span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-end gap-2">
                        {!block.isPending && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 hover:bg-white/10 hover:text-red-500"
                            onClick={() => {
                              if (block.type === 'date') {
                                deleteBlockedDate.mutate(block.rawId);
                              } else {
                                deleteBlockedTimeSlot.mutate(block.rawId);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        {block.isPending && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 hover:bg-white/10 hover:text-red-500"
                            onClick={() => {
                              handleRemovePending(
                                block.type === 'date' ? 'date' : 'slot',
                                block.pendingIndex!
                              );
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {upcomingBlocks.length === 0 && !loadingDates && !loadingSlots && (
              <div className="text-center py-8 text-gray-400">
                No blocked dates or times scheduled
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 mt-8">
            <Button
              variant="outline"
              onClick={() => {
                if (hasChanges) {
                  if (confirm("You have unsaved changes. Are you sure you want to cancel?")) {
                    setLocation("/admin/bookings");
                  }
                } else {
                  setLocation("/admin/bookings");
                }
              }}
              className="border-gray-600 hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              className="bg-white hover:bg-white/90 text-black"
              onClick={() => saveChangesMutation.mutate()}
              disabled={!hasChanges || saveChangesMutation.isPending}
            >
              {saveChangesMutation.isPending ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>

        {/* Modals */}
        {showDateBlockModal && (
          <DateBlockModal
            isOpen={showDateBlockModal}
            onClose={() => setShowDateBlockModal(false)}
            onSave={handleAddDates}
            existingBlockedDates={allBlockedDates}
          />
        )}
        
        {showTimeBlockModal && (
          <TimeBlockModal
            isOpen={showTimeBlockModal}
            onClose={() => setShowTimeBlockModal(false)}
            onSave={handleAddTimeSlots}
          />
        )}
      </main>
    </div>
  );
}